import type { ReactNode } from "react";
import { Field, type TypedWrappedFieldProps } from "redux-form";

import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CheckIcon,
  FlagIcon,
  LineChartIcon,
  LinkedInIcon,
  LockSmallIcon,
  PostRightIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";

type SettingOptionData = {
  label: string;
  value: boolean | "null";
  description: string;
  icon: ReactNode;
  selectedIconColor: string;
};

const LINKEDIN_POSTING_OPTIONS: SettingOptionData[] = [
  {
    label: "Force Enabled",
    value: true,
    description: "The job will always be published to LinkedIn.",
    icon: <FlagIcon sx={{ fontSize: "21px" }} />,
    selectedIconColor: "var(--positive-1)",
  },
  {
    label: "Default",
    value: "null",
    description: "Posting is decided automatically based on internal rules.",
    icon: (
      <LineChartIcon
        sx={{ fontSize: "21px", path: { stroke: "currentColor" } }}
      />
    ),
    selectedIconColor: "var(--info-2)",
  }, // Toggle button does not accept null as value
  {
    label: "Disabled",
    value: false,
    description: "The job will not be published to LinkedIn.",
    icon: <LockSmallIcon sx={{ fontSize: "17px", ml: "2px", mr: "2px" }} />,
    selectedIconColor: "var(--negative-2)",
  },
];

const VONQ_POSTING_OPTIONS: SettingOptionData[] = [
  {
    label: "Force Enabled",
    value: true,
    description: "The job will always be published to VonQ.",
    icon: <FlagIcon sx={{ fontSize: "21px" }} />,
    selectedIconColor: "var(--positive-1)",
  },
  {
    label: "Default",
    value: "null",
    description: "Posting is decided automatically based on internal rules.",
    icon: (
      <LineChartIcon
        sx={{ fontSize: "21px", path: { stroke: "currentColor" } }}
      />
    ),
    selectedIconColor: "var(--info-2)",
  }, // Toggle button does not accept null as value
  {
    label: "Disabled",
    value: false,
    description: "The job will not be published to VonQ.",
    icon: <LockSmallIcon sx={{ fontSize: "17px", ml: "2px", mr: "2px" }} />,
    selectedIconColor: "var(--negative-2)",
  },
];

export const ExternalJobPostingPopoverButton = () => {
  const isNodeStaffOrImpersonated = useIsNodeOrImpersonated();
  if (!isNodeStaffOrImpersonated) {
    return <></>;
  }

  return (
    <Popover
      anchor={
        <Button
          variant="white-violet"
          shape="squared"
          startIcon={<PostRightIcon />}
        >
          External Posting
        </Button>
      }
    >
      <Stack sx={{ p: 3 }}>
        <Typography component="p" sx={{ mb: 1.5 }}>
          <LinkedInIcon
            sx={{
              color: "#0a66c2",
              fontSize: "20px",
              verticalAlign: "sub",
              mr: 0.75,
            }}
          />
          LinkedIn visibility
        </Typography>
        <Field
          name="automatic_linkedin_posting_enabled"
          component={PostingField}
          options={LINKEDIN_POSTING_OPTIONS}
        />
        <Divider sx={{ my: 2.5 }} color="beige" />
        <Typography component="p" sx={{ mb: 1.5 }}>
          <Box
            component="span"
            sx={{
              width: "16.5px",
              height: "16.5px",
              borderRadius: "2px",
              backgroundColor: "#B92554",
              display: "inline-block",
              position: "relative",
              mb: "-3px",
              mr: 0.75,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "block",
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                color: "var(--white)",
                fontWeight: "500",
                fontSize: "12px",
              }}
            >
              Q
            </Box>
          </Box>
          VonQ visibility
        </Typography>
        <Field
          name="automatic_vonq_posting_enabled"
          component={PostingField}
          options={VONQ_POSTING_OPTIONS}
        />
      </Stack>
    </Popover>
  );
};

type PostingFieldProps = TypedWrappedFieldProps<boolean | null> & {
  options: SettingOptionData[];
};

const PostingField = (props: PostingFieldProps) => {
  const mappedValue =
    typeof props.input.value !== "boolean" ? "null" : props.input.value;
  const handleChange = (value: boolean | "null") => {
    props.input.onChange(value === "null" ? null : value);
  };

  return (
    <Stack sx={{ gap: 1 }}>
      {props.options.map((option) => {
        const isSelected = mappedValue === option.value;
        return (
          <SettingOption
            key={option.label}
            {...option}
            isSelected={isSelected}
            onClick={() => {
              handleChange(option.value);
            }}
          />
        );
      })}
    </Stack>
  );
};

export type SettingOptionProps = {
  icon: ReactNode;
  label: string;
  description: string;
  value: unknown;
  selectedIconColor?: string;
  isSelected: boolean;
  onClick?: () => void;
};

const SettingOption = ({
  icon,
  label,
  description,
  isSelected,
  selectedIconColor,
  onClick,
}: SettingOptionProps) => {
  return (
    <Button
      variant="transparent"
      onClick={onClick}
      shape="squared"
      sx={{
        minWidth: "411px !important",
        justifyContent: "start",
        p: "12px !important",
        backgroundColor: isSelected ? "var(--soft-blue) !important" : undefined,
      }}
      aria-label={label}
    >
      <Stack sx={{ width: "100%" }}>
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Stack
            direction="row"
            sx={{
              mr: 1,
              alignItems: "center",
              color:
                isSelected && selectedIconColor ? selectedIconColor : "inherit",
            }}
          >
            {icon}
          </Stack>
          <Typography component="p" variant="label" size="small">
            {label}
          </Typography>
        </Stack>
        <Stack direction="row">
          <Typography component="p" variant="paragraph" size="small">
            {description}
          </Typography>
          {isSelected && <CheckIcon sx={{ ml: 1, fontSize: 21 }} />}
        </Stack>
      </Stack>
    </Button>
  );
};
